import { useEffect, useState } from 'react'
import { Coins, ExternalLink, Mail, ShieldCheck, TriangleAlert } from 'lucide-react'
import { Link } from '../router.js'
import { useConfirm } from '../shell/confirm.js'
import { Field, TextInput } from './form.js'
import { adminMutate, apiGet, usePermissions, useToast } from './primitives.js'
import './admin.css'

/**
 * ZAP SIS — LA BOÎTE MAIL DES RAPPORTS DE CAISSE.
 *
 * Routé sur /settings/integrations/zap-sis.
 *
 * ═══ CE QUE CET ÉCRAN PROMET ═══
 *
 * Dire ce qui EST. Le bandeau d'état ne connaît que quatre réponses — non
 * configuré, à réauthentifier, connecté, en erreur — et elles viennent du
 * serveur, jamais d'une supposition d'interface. Un « Connecté » affiché à
 * tort coûterait une nuit : on croirait les rapports arrivés, la clôture se
 * ferait sans eux, et personne ne chercherait de ce côté-là.
 *
 * ═══ DEUX PHRASES QU'ON REFUSE DE LAISSER CROIRE ═══
 *
 * 1. « Google ne nous donne que les messages [ZAP] ». FAUX. Le jeton ouvre la
 *    boîte ENTIÈRE en lecture ; c'est Gestion qui écarte tout ce qui ne porte
 *    pas le marqueur. La restriction est APPLICATIVE, et l'écran le dit.
 * 2. « Gestion range la boîte ». FAUX aussi, et volontairement : rien n'est
 *    marqué lu, archivé, étiqueté ni supprimé. Ce qui a été traité est
 *    mémorisé DANS Gestion.
 *
 * ═══ AUCUN SECRET À L'ÉCRAN ═══
 *
 * Ni jeton, ni identifiant client, ni secret, ni empreinte de secret. La
 * réponse du serveur ne porte qu'un booléen « des identifiants existent ».
 */

interface Anomalies { sans_piece_jointe: number; en_echec: number }

/** La seconde source de la boîte : les redditions Shotgun transférées. Borne et erreurs à elles. */
interface RedditionsShotgun {
  enabled: boolean
  expected_sender_domain: string
  subject_filter: string
  ingest_since: string
  last_message_at: string | null
  last_document_id: string | null
  last_document_at: string | null
  last_error: string | null
}

interface EtatBoite {
  state: 'NOT_CONFIGURED' | 'NEEDS_AUTH' | 'CONNECTED' | 'ERROR'
  state_label: string
  credentials_configured: boolean
  scope: string
  read_only: boolean
  expected_address: string
  connected_address: string | null
  account_mismatch: boolean
  connected_at: string | null
  subject_filter: string
  filter_is_applicative: boolean
  ingest_since: string | null
  last_sync_at: string | null
  last_message_at: string | null
  last_document_id: string | null
  last_document_at: string | null
  last_error: string | null
  shotgun: RedditionsShotgun
  anomalies: Anomalies
  redirect_uri: string
}

interface MessageReleve {
  id: string
  gmail_message_id: string
  received_at: string
  sender: string | null
  subject: string | null
  attachment_count: number
  status: 'SEEN' | 'IMPORTED' | 'NO_ATTACHMENT' | 'DUPLICATE' | 'FAILED'
  note: string | null
  document_id: string | null
  document_filename: string | null
}

const PILULE: Record<EtatBoite['state'], string> = {
  NOT_CONFIGURED: 'lc-pill lc-pill--idle',
  NEEDS_AUTH: 'lc-pill lc-pill--warning',
  CONNECTED: 'lc-pill lc-pill--ok',
  ERROR: 'lc-pill lc-pill--error',
}

const STATUT_MESSAGE: Record<MessageReleve['status'], { texte: string; classe: string }> = {
  IMPORTED: { texte: 'Rapport importé', classe: 'lc-pill lc-pill--ok' },
  DUPLICATE: { texte: 'Déjà connu', classe: 'lc-pill lc-pill--idle' },
  NO_ATTACHMENT: { texte: 'Sans pièce jointe', classe: 'lc-pill lc-pill--warning' },
  FAILED: { texte: 'Échec', classe: 'lc-pill lc-pill--error' },
  SEEN: { texte: 'Vu', classe: 'lc-pill lc-pill--idle' },
}

/** Messages du retour OAuth — un par cause RÉELLE renvoyée par le serveur. */
const OAUTH_RETOURS: Record<string, { ok: boolean; texte: string }> = {
  ok: { ok: true, texte: 'Boîte reliée — le consentement Google a été finalisé.' },
  refuse: { ok: false, texte: 'Connexion annulée ou refusée chez Google — rien n’a été modifié.' },
  STATE_INVALIDE: { ok: false, texte: 'La connexion n’a pas pu être finalisée : état de sécurité inconnu ou expiré. Relancez la connexion.' },
  IDENTIFIANTS_ABSENTS: { ok: false, texte: 'La connexion n’a pas pu être finalisée : identifiants OAuth absents du coffre.' },
  ECHANGE_REFUSE: { ok: false, texte: 'La connexion n’a pas pu être finalisée : Google a refusé l’échange du code. Vérifiez l’URI de redirection et le secret client.' },
  REFRESH_ABSENT: { ok: false, texte: 'Google n’a pas fourni d’accès permanent. Retirez l’accès « Gestion » dans myaccount.google.com → Sécurité, puis reconnectez.' },
}

/** Lit puis EFFACE ?oauth= : le verdict s'affiche une fois, pas à chaque navigation. */
function verdictOAuthDepuisUrl(): { ok: boolean; texte: string } | null {
  if (typeof window === 'undefined') return null
  const qp = new URLSearchParams(window.location.search)
  const brut = qp.get('oauth')
  if (!brut) return null
  const cause = qp.get('cause')
  qp.delete('oauth'); qp.delete('cause')
  const reste = qp.toString()
  window.history.replaceState(null, '', window.location.pathname + (reste ? `?${reste}` : ''))
  return OAUTH_RETOURS[brut === 'erreur' ? (cause ?? 'erreur') : brut]
    ?? { ok: false, texte: 'La connexion n’a pas pu être finalisée.' }
}

function quand(iso: string | null): string {
  if (!iso) return 'jamais'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? 'date illisible' : d.toLocaleString('fr-FR')
}

export function ZapIntegrationPage() {
  const toast = useToast()
  const confirm = useConfirm()
  const perms = usePermissions()
  const peutConnecter = perms.permissions.includes('finance.source.email.connect')
  const peutRelever = perms.permissions.includes('finance.source.email.sync')

  const [etat, setEtat] = useState<EtatBoite | null>(null)
  const [messages, setMessages] = useState<MessageReleve[]>([])
  const [erreurChargement, setErreurChargement] = useState<string | null>(null)
  const [verdictOAuth] = useState(verdictOAuthDepuisUrl)
  const [occupe, setOccupe] = useState(false)

  const charger = () => {
    void apiGet<EtatBoite | { error: string; message?: string }>('/api/v1/finance/email/config')
      .then((j) => {
        if ('error' in j) setErreurChargement(j.message ?? 'Lecture impossible.')
        else { setEtat(j); setErreurChargement(null) }
      })
      .catch(() => setErreurChargement('Gestion n’a pas répondu.'))
    void apiGet<{ messages?: MessageReleve[] }>('/api/v1/finance/email/messages?limit=15')
      .then((j) => setMessages(j.messages ?? []))
      .catch(() => { /* la liste est un confort : son absence ne masque pas l'état */ })
  }
  useEffect(charger, [])

  /* ─── identifiants OAuth : posés au coffre, jamais relus ─── */
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const enregistrerIdentifiants = async () => {
    setOccupe(true)
    const r = await adminMutate<{ error?: string; message?: string }>(
      'PUT', '/api/v1/finance/email/credentials',
      { client_id: clientId.trim(), client_secret: clientSecret.trim() })
    setOccupe(false)
    if (r.status === 200) { toast('ok', 'Identifiants enregistrés au coffre.'); setClientSecret(''); charger() }
    else toast('error', r.data.message ?? 'Enregistrement impossible.')
  }

  /* ─── réglages administrables ─── */
  const [adresse, setAdresse] = useState<string | null>(null)
  const [filtre, setFiltre] = useState<string | null>(null)
  const adresseCourante = adresse ?? etat?.expected_address ?? ''
  const filtreCourant = filtre ?? etat?.subject_filter ?? ''
  const reglagesModifies = etat !== null
    && (adresseCourante.trim() !== etat.expected_address || filtreCourant.trim() !== etat.subject_filter)
  const enregistrerReglages = async () => {
    setOccupe(true)
    const r = await adminMutate<{ error?: string; message?: string }>(
      'PUT', '/api/v1/finance/email/settings',
      { expected_address: adresseCourante.trim(), subject_filter: filtreCourant.trim() })
    setOccupe(false)
    if (r.status === 200) { toast('ok', 'Réglages enregistrés.'); setAdresse(null); setFiltre(null); charger() }
    else toast('error', r.data.message ?? 'Enregistrement impossible.')
  }

  /* ─── gestes ─── */
  const connecter = async () => {
    const r = await adminMutate<{ error?: string; url?: string; message?: string }>(
      'POST', '/api/v1/finance/email/oauth/start')
    if (r.status === 200 && r.data.url) window.location.href = r.data.url
    else toast('error', r.data.message ?? 'Impossible de démarrer la connexion.')
  }

  const tester = async () => {
    setOccupe(true)
    const r = await adminMutate<{ error?: string; message?: string; ok?: boolean; mismatch?: boolean }>(
      'POST', '/api/v1/finance/email/test')
    setOccupe(false)
    if (r.status !== 200) { toast('error', r.data.message ?? 'Test impossible.'); return }
    // Un compte différent de l'attendu n'est PAS une réussite tranquille : le
    // test répond, mais l'écran doit le signaler comme un avertissement.
    toast(r.data.ok && !r.data.mismatch ? 'ok' : 'error', r.data.message ?? 'Test effectué.')
    charger()
  }

  const relever = async () => {
    setOccupe(true)
    const r = await adminMutate<{
      error?: string; message?: string; ran?: boolean; reason?: string | null
      messagesNouveaux?: number; documentsImportes?: number; erreur?: string | null
    }>('POST', '/api/v1/finance/email/sync')
    setOccupe(false)
    if (r.status !== 200) { toast('error', r.data.message ?? 'Relève impossible.'); return }
    if (r.data.ran === false) { toast('error', r.data.reason ?? 'Relève non exécutée.'); charger(); return }
    const bilan = `${r.data.messagesNouveaux ?? 0} nouveau(x) message(s), ${r.data.documentsImportes ?? 0} rapport(s) importé(s)`
    // Une relève ZAP aboutie peut porter l'échec d'une autre source : il se dit.
    if (r.data.erreur) toast('error', `Relève terminée avec une erreur — ${r.data.erreur} (${bilan}).`)
    else toast('ok', `Relève terminée : ${bilan}.`)
    charger()
  }

  const [jours, setJours] = useState('7')
  const reprendre = async () => {
    const n = Number(jours)
    if (!Number.isInteger(n) || n < 1 || n > 730) { toast('error', 'Indiquez un nombre de jours entre 1 et 730.'); return }
    if (!(await confirm({
      title: `Reprendre les ${n} derniers jours ?`,
      message: 'Gestion relira les messages marqués de cette période et importera les pièces jointes qu’elle ne connaît pas encore. Les rapports déjà reçus ne seront pas dupliqués.',
      variant: 'sensitive', confirmLabel: 'Lancer la reprise',
    }))) return
    setOccupe(true)
    const r = await adminMutate<{ error?: string; message?: string; messagesNouveaux?: number; documentsImportes?: number }>(
      'POST', '/api/v1/finance/email/backfill', { days: n })
    setOccupe(false)
    if (r.status !== 200) { toast('error', r.data.message ?? 'Reprise impossible.'); return }
    toast('ok', `Reprise terminée : ${r.data.messagesNouveaux ?? 0} message(s), ${r.data.documentsImportes ?? 0} rapport(s) importé(s).`)
    charger()
  }

  const delier = async () => {
    if (!(await confirm({
      title: 'Délier la boîte mail ?',
      message: 'L’autorisation est révoquée chez Google et le jeton supprimé du coffre. Les rapports déjà reçus restent en place — c’est l’arrivée automatique qui s’arrête.',
      variant: 'critical', confirmLabel: 'Délier',
    }))) return
    setOccupe(true)
    const r = await adminMutate<{ error?: string; message?: string; revoked_at_google?: boolean }>(
      'POST', '/api/v1/finance/email/disconnect')
    setOccupe(false)
    if (r.status !== 200) { toast('error', r.data.message ?? 'Déconnexion impossible.'); return }
    toast(r.data.revoked_at_google ? 'ok' : 'error', r.data.revoked_at_google
      ? 'Boîte déliée et autorisation révoquée chez Google.'
      : 'Jeton supprimé côté Gestion, mais la révocation chez Google n’a pas pu être confirmée : retirez l’accès dans myaccount.google.com.')
    charger()
  }

  return (
    <main className="lc-page lc-stagger">
      <Link className="lc-crumb" href="/settings/integrations">← Intégrations</Link>
      <header className="fh-hero">
        <span className="fh-hero__icon" aria-hidden="true"><Coins size={26} /></span>
        <div>
          <h1 className="lc-title">ZAP SIS</h1>
          <p className="lc-subtitle">
            Les rapports de caisse, reçus par courriel.{' '}
            {etat ? <span className={PILULE[etat.state]}>{etat.state_label}</span> : null}
          </p>
        </div>
      </header>

      {verdictOAuth ? <p className="lc-hint" role="status">{verdictOAuth.texte}</p> : null}
      {erreurChargement ? <p className="lc-hint">{erreurChargement}</p> : null}

      {etat ? (
        <>
          {/* ─── ce qui est vrai de la connexion ─── */}
          <section className="lc-panel">
            <header className="lc-panel__head"><h2 className="lc-section-title">La connexion</h2></header>
            <div className="lc-panel__body">
              {etat.account_mismatch ? (
                <p className="lc-hint" role="alert">
                  <TriangleAlert size={14} aria-hidden="true" /> Le compte relié est{' '}
                  <strong>{etat.connected_address}</strong>, alors que l’adresse attendue est{' '}
                  <strong>{etat.expected_address}</strong>. Les rapports seront cherchés dans la boîte
                  réellement reliée — pas dans celle qu’on attendait.
                </p>
              ) : null}
              <div className="ad-facts">
                <div className="ad-fact">
                  <span className="ad-fact__label">Compte relié</span>
                  <span className="ad-fact__value">
                    {etat.connected_address ?? (etat.state === 'CONNECTED' ? 'identité pas encore relevée' : 'aucun')}
                  </span>
                </div>
                <div className="ad-fact">
                  <span className="ad-fact__label">Adresse attendue</span>
                  <span className="ad-fact__value">{etat.expected_address}</span>
                </div>
                <div className="ad-fact">
                  <span className="ad-fact__label">Accès demandé à Google</span>
                  <span className="ad-fact__value">
                    <ShieldCheck size={14} aria-hidden="true" /> Lecture seule des messages
                  </span>
                </div>
                <div className="ad-fact">
                  <span className="ad-fact__label">Filtre appliqué</span>
                  <span className="ad-fact__value">objet contenant « {etat.subject_filter} »</span>
                </div>
                <div className="ad-fact">
                  <span className="ad-fact__label">Relève automatique</span>
                  <span className="ad-fact__value">{etat.state === 'CONNECTED' ? 'toutes les 2 minutes' : 'à l’arrêt'}</span>
                </div>
                <div className="ad-fact">
                  <span className="ad-fact__label">Dernière relève</span>
                  <span className="ad-fact__value">{quand(etat.last_sync_at)}</span>
                </div>
                <div className="ad-fact">
                  <span className="ad-fact__label">Dernier rapport importé</span>
                  <span className="ad-fact__value">
                    {etat.last_document_id
                      ? <Link href={`/comptabilite/sources/${etat.last_document_id}`}>{quand(etat.last_document_at)}</Link>
                      : 'aucun'}
                  </span>
                </div>
                <div className="ad-fact">
                  <span className="ad-fact__label">Redditions Shotgun</span>
                  <span className="ad-fact__value">
                    {etat.shotgun.enabled
                      ? <>relevées · expéditeur …@{etat.shotgun.expected_sender_domain} · dernier message {quand(etat.shotgun.last_message_at)}</>
                      : 'non relevées'}
                    {etat.shotgun.enabled && etat.shotgun.last_document_id
                      ? <> · <Link href={`/comptabilite/sources/${etat.shotgun.last_document_id}`}>dernière reddition</Link></>
                      : null}
                  </span>
                </div>
                {etat.shotgun.last_error ? (
                  <div className="ad-fact">
                    <span className="ad-fact__label">Dernière erreur Shotgun</span>
                    <span className="ad-fact__value">{etat.shotgun.last_error}</span>
                  </div>
                ) : null}
                <div className="ad-fact">
                  <span className="ad-fact__label">Messages signalés</span>
                  <span className="ad-fact__value">
                    {etat.anomalies.sans_piece_jointe} sans pièce jointe · {etat.anomalies.en_echec} en échec
                  </span>
                </div>
                {etat.last_error ? (
                  <div className="ad-fact">
                    <span className="ad-fact__label">Dernière erreur</span>
                    <span className="ad-fact__value">{etat.last_error}</span>
                  </div>
                ) : null}
              </div>

              {/* La phrase qui empêche le malentendu le plus coûteux. */}
              <p className="lc-hint">
                <Mail size={14} aria-hidden="true" /> Gestion lit la boîte, elle ne la touche pas : aucun message
                n’est marqué lu, archivé, étiqueté ni supprimé — ce qui a déjà été traité est mémorisé ici, dans
                Gestion. Et le tri sur « {etat.subject_filter} » — comme celui des redditions Shotgun sur leur
                expéditeur — est fait <strong>par Gestion</strong> : l’autorisation Google porte sur la boîte entière
                en lecture, ce n’est pas Google qui filtre.
              </p>

              <div className="ad-actions">
                {etat.state === 'CONNECTED' && peutRelever ? (
                  <>
                    <button type="button" className="lc-btn" disabled={occupe} onClick={tester}>Tester la connexion</button>
                    <button type="button" className="lc-btn lc-btn--primary" disabled={occupe} onClick={relever}>Relever maintenant</button>
                  </>
                ) : null}
                {(etat.state === 'NEEDS_AUTH' || etat.state === 'ERROR') && peutConnecter ? (
                  <button type="button" className="lc-btn lc-btn--primary" disabled={occupe} onClick={connecter}>
                    {etat.state === 'ERROR' ? 'Reconnecter la boîte' : 'Autoriser Gestion'} <ExternalLink size={14} aria-hidden="true" />
                  </button>
                ) : null}
                {etat.state === 'CONNECTED' && peutConnecter ? (
                  <button type="button" className="lc-btn lc-btn--danger" disabled={occupe} onClick={delier}>Délier la boîte</button>
                ) : null}
              </div>
            </div>
          </section>

          {/* ─── identifiants OAuth : visibles tant qu'ils manquent ─── */}
          {!etat.credentials_configured && peutConnecter ? (
            <section className="lc-panel">
              <header className="lc-panel__head"><h2 className="lc-section-title">1 · Identifiants OAuth Google</h2></header>
              <div className="lc-panel__body">
                <p className="lc-hint">
                  Créez un « ID client OAuth » de type <em>application Web</em> dans la console Google Cloud, et
                  déclarez-y l’URI de redirection ci-dessous. Utilisez de préférence un client DISTINCT de celui de
                  la fiche Google Business : c’est ce qui permet de révoquer l’un sans toucher l’autre.
                </p>
                <Field label="URI de redirection à déclarer chez Google" helper="Valeur canonique fournie par le serveur — à coller telle quelle.">
                  <TextInput value={etat.redirect_uri} onChange={() => undefined} disabled ariaLabel="URI de redirection" />
                </Field>
                <Field label="Client ID OAuth" helper="Il ne sera ni réaffiché ni relu ensuite : Gestion se contentera de confirmer qu’il existe.">
                  <TextInput value={clientId} onChange={setClientId} placeholder="xxxxxxxx.apps.googleusercontent.com" />
                </Field>
                <Field label="Client secret" helper="Posé au coffre chiffré — jamais relu, jamais journalisé, jamais réaffiché.">
                  <input className="lc-input" type="password" value={clientSecret} autoComplete="new-password"
                    name="zap_client_secret" aria-label="Client secret"
                    onChange={(e) => setClientSecret(e.target.value)} />
                </Field>
                <div className="ad-actions">
                  <button type="button" className="lc-btn lc-btn--primary"
                    disabled={occupe || clientId.trim().length < 10 || clientSecret.trim().length < 10}
                    onClick={enregistrerIdentifiants}>Enregistrer au coffre</button>
                </div>
              </div>
            </section>
          ) : null}

          {/* ─── réglages administrables ─── */}
          {peutConnecter ? (
            <section className="lc-panel">
              <header className="lc-panel__head"><h2 className="lc-section-title">Réglages de la boîte</h2></header>
              <div className="lc-panel__body">
                <Field label="Adresse attendue" helper="Celle qui reçoit les rapports. Si le compte réellement relié diffère, l’écran le dit plutôt que de le taire.">
                  <TextInput value={adresseCourante} onChange={setAdresse} maxLength={200} ariaLabel="Adresse attendue" />
                </Field>
                <Field label="Marqueur dans l’objet" helper="Un message dont l’objet ne contient pas ce marqueur n’est jamais traité par la Comptabilité. Le marqueur dit que le message nous est destiné — pas quel rapport est joint.">
                  <TextInput value={filtreCourant} onChange={setFiltre} maxLength={60} ariaLabel="Marqueur dans l’objet" />
                </Field>
                <div className="ad-actions">
                  <button type="button" className="lc-btn lc-btn--primary" disabled={occupe || !reglagesModifies}
                    onClick={enregistrerReglages}>Enregistrer les réglages</button>
                </div>
              </div>
            </section>
          ) : null}

          {/* ─── reprise : un geste demandé, jamais un balayage d'office ─── */}
          {etat.state === 'CONNECTED' && peutRelever ? (
            <section className="lc-panel">
              <header className="lc-panel__head"><h2 className="lc-section-title">Reprise sur une période</h2></header>
              <div className="lc-panel__body">
                <p className="lc-hint">
                  À la première connexion, Gestion ne remonte pas le temps : elle prend les messages{' '}
                  <strong>à partir de ce moment-là</strong>. Reprendre un historique reste un geste demandé, jamais
                  un balayage automatique de toute la boîte.
                  {etat.ingest_since ? ` Ingestion depuis le ${quand(etat.ingest_since)}.` : ''}
                </p>
                <Field label="Profondeur de reprise (en jours)" helper="Entre 1 et 730. Les rapports déjà reçus ne sont pas dupliqués : la déduplication porte sur le contenu du fichier.">
                  <TextInput value={jours} onChange={setJours} maxLength={3} ariaLabel="Nombre de jours" />
                </Field>
                <div className="ad-actions">
                  <button type="button" className="lc-btn" disabled={occupe} onClick={reprendre}>Reprendre cette période</button>
                </div>
              </div>
            </section>
          ) : null}

          {/* ─── ce qui est réellement arrivé, anomalies comprises ─── */}
          <section className="lc-panel">
            <header className="lc-panel__head"><h2 className="lc-section-title">Derniers messages relevés</h2></header>
            <div className="lc-panel__body">
              {messages.length === 0 ? (
                <p className="lc-hint">Aucun message relevé pour l’instant.</p>
              ) : (
                <table className="lc-table">
                  <thead>
                    <tr>
                      <th scope="col">Reçu le</th>
                      <th scope="col">Expéditeur</th>
                      <th scope="col">Objet</th>
                      <th scope="col">Pièces</th>
                      <th scope="col">Résultat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map((m) => (
                      <tr key={m.id}>
                        <td>{quand(m.received_at)}</td>
                        <td>{m.sender ?? '—'}</td>
                        <td>{m.subject ?? '—'}</td>
                        <td>{m.attachment_count}</td>
                        <td>
                          <span className={STATUT_MESSAGE[m.status].classe}>{STATUT_MESSAGE[m.status].texte}</span>
                          {m.document_id ? (
                            <> <Link href={`/comptabilite/sources/${m.document_id}`}>{m.document_filename ?? 'le document'}</Link></>
                          ) : null}
                          {m.note && !m.document_id ? <div className="lc-hint">{m.note}</div> : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </>
      ) : !erreurChargement ? <p className="lc-hint">Lecture de l’état…</p> : null}
    </main>
  )
}
